import { useState, useEffect } from "react";
//@ts-ignore
import Twemoji from "react-twemoji";
import { Channel } from "types";

interface Region {
    name: string;
    value: string;
    flag: string;
    default?: boolean;
}

interface VoiceSettingsProps {
    channel: Channel;
}

const availableRegions: Region[] = [
    { name: "US West (N. California)", value: "us-west-1", flag: "🇺🇸" },
    { name: "US East (N. Virginia)", value: "us-east-1", flag: "🇺🇸", default: true },
    { name: "US East (Ohio)", value: "us-east-2", flag: "🇺🇸" },
    { name: "US West (Oregon)", value: "us-west-2", flag: "🇺🇸" },
    { name: "Europe (Ireland)", value: "eu-west-1", flag: "🇪🇺" },
    { name: "Europe (London)", value: "eu-west-2", flag: "🇪🇺" },
    { name: "Europe (Paris)", value: "eu-west-3", flag: "🇪🇺" },
    { name: "Europe (Frankfurt)", value: "eu-central-1", flag: "🇪🇺" },
    { name: "Europe (Stockholm)", value: "eu-north-1", flag: "🇪🇺" },
    { name: "Europe (Milan)", value: "eu-south-1", flag: "🇪🇺" },
    { name: "Asia Pacific (Mumbai)", value: "ap-south-1", flag: "🇮🇳" },
    { name: "Asia Pacific (Tokyo)", value: "ap-northeast-1", flag: "🇯🇵" },
    { name: "Asia Pacific (Seoul)", value: "ap-northeast-2", flag: "🇰🇷" },
    { name: "Asia Pacific (Singapore)", value: "ap-southeast-1", flag: "🇸🇬" },
    { name: "Asia Pacific (Sydney)", value: "ap-southeast-2", flag: "🇦🇺" },
    { name: "Asia Pacific (Hong Kong)", value: "ap-east-1", flag: "🇭🇰" },
    { name: "Canada (Central)", value: "ca-central-1", flag: "🇨🇦" },
];

const VoiceSettings: React.FC<VoiceSettingsProps> = ({ channel }) => {
    const [selectedRegion, setSelectedRegion] = useState<string>(availableRegions.find(region => region.default)?.value || "");
    const [regionPings, setRegionPings] = useState<{ [key: string]: number }>({});
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const changeRegion = (region: string) => {
        setDropdownOpen(false);

        setSelectedRegion(region);

        fetch(`__SITE_BASE__/api/teams/${channel.teamId}/channels/${channel.id}/region`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ region }),
        });
        
    };

    useEffect(() => {
        const fetchPings = async () => {
            const newPings: { [key: string]: number } = {};
            await Promise.all(
                availableRegions.map(async (region) => {
                    const start = Date.now();
                    try {
                        await fetch(`https://ec2.${region.value}.amazonaws.com/ping`, {
                            method: "GET",
                            mode: "no-cors",
                        });
                        newPings[region.value] = Date.now() - start;
                    } catch {
                        newPings[region.value] = -1;
                    }
                })
            );
            setRegionPings(newPings);
        };

        fetchPings();
    }, []);

    const getPingColor = (ping: number) => {
        if (ping === -1) return "-red-500";
        if (ping < 100) return "-green-500";
        if (ping < 200) return "-yellow-500";
        return "-orange-500";
    };

    return (
        <div className="flex flex-col w-full space-y-4 rounded-lg">
            <h1 className="text-gray-200 font-semibold text-lg">Voice Settings</h1>
            <p className="text-gray-400">Change the voice region for this channel.</p>
            <div className="relative">
                <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="w-full flex items-center justify-between px-4 py-2 text-gray-200 rounded-lg bg-brand-profile focus:outline-none"
                >
                    <div className="flex items-center space-x-2">
                        <div className="max-w-4">
                            <Twemoji options={{ className: "twemoji" }}>
                                {availableRegions.find(region => region.value === selectedRegion)?.flag || "❓"}
                            </Twemoji>
                        </div>
                        <span>{availableRegions.find(region => region.value === selectedRegion)?.name || "Select a region"}</span>
                        {regionPings[selectedRegion] !== undefined && (
                            <div className={`w-2 h-2 rounded-full bg${getPingColor(regionPings[selectedRegion] || -1)}`} />
                        )}
                    </div>
                    <svg
                        className={`w-5 h-5 transform ${dropdownOpen ? "rotate-180" : "rotate-0"}`}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
                {dropdownOpen && (
                    <div className="absolute z-10 w-full mt-2 bg-brand-default rounded shadow-2xl max-h-96 overflow-y-auto" style={{ top: "calc(100% + 0.5rem)", scrollbarWidth: "none" }}>
                        {availableRegions
                            .sort((a, b) => (regionPings[a.value] || 0) - (regionPings[b.value] || 0))
                            .map((region) => (
                                <div
                                    key={region.value}
                                    className={`flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-brand-back ${selectedRegion === region.value ? "bg-brand-profile" : ""
                                        }`}
                                    onClick={() => changeRegion(region.value)}
                                >
                                    <div className="flex items-center space-x-2">
                                        <div className="max-w-4">
                                            <Twemoji options={{ className: "twemoji" }}>
                                                <span>{region.flag}</span>
                                            </Twemoji>
                                        </div>
                                        <span>{region.name}</span>
                                    </div>
                                    <div className="flex gap-2 items-center">
                                        <div className={`w-2 h-2 rounded-full bg${getPingColor(regionPings[region.value] || -1)}`} />
                                        <span className={`text${getPingColor(regionPings[region.value] || -1)}`}>
                                            {regionPings[region.value] === -1 ? "N/A" : `${regionPings[region.value]}ms`}
                                        </span>
                                    </div>
                                </div>
                            ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default VoiceSettings;